import { useSelector } from "react-redux";
import type { RootState } from "../redux/store";
import type { LocationPros } from "../types/baseTypes";
import { cityOf, districtOf } from "./locationAddress";
import { STATION_TIME_ZONE, zoneOfLocation } from "./stationTime";
import { flowUseCase } from "./bookingFlowUseCase";
import { isRoomFamily } from "./useCases";

/**
 * The zone a set of locations shares — the default when they do not agree or
 * there are none. A search area (a city, a district) is one place, so its
 * locations share a clock; were they ever to disagree there is no single
 * window to search with, and the default is what the server assumes for a
 * request without a location.
 */
export const sharedZoneOf = (locations: readonly LocationPros[]): string => {
  const zones = new Set(locations.map((location) => zoneOfLocation(location)));
  return zones.size === 1 ? Array.from(zones)[0] : STATION_TIME_ZONE;
};

/**
 * The zone of the location the customer is booking at (#3219) — the ONE place
 * the booking flow reads it. Every booking step, quote and request window runs
 * on it, because the server validates nights, the booking window and opening
 * hours in the same zone; an app on another clock offers days the server then
 * refuses.
 *
 * In order:
 *   1. in a room/venue flow (`bookingCreation.useCase` "room", "venue" or
 *      "apartment"): the location of the picked room/venue
 *      (`apartment.selectedApartment`) — the object actually booked. It ranks
 *      ABOVE 2. there because a room search with a district chosen never
 *      copies it into 2., so a `selectedLocation` left from before a district
 *      change could otherwise win;
 *   2. the selected location (`bookingCreation.selectedLocation`, a `GET
 *      location` row carrying `timeZone`) — every station flow sets it;
 *   3. the room search area (city, and district unless "all") — a room search
 *      runs before any one location is picked;
 *   4. the default zone.
 *
 * A screen that holds its OWN location or booking (a room week, an order)
 * passes that one's zone through `zoneOfLocation` / `locationTimeZone`
 * instead — never this selection.
 */

export const selectSelectedLocationTimeZone = (state: RootState): string => {
  // Optional reads throughout: a test store (and a slice not yet hydrated) may
  // lack any of these, and an absent piece means "not known yet", never a crash.
  const selectedLocation = state.bookingCreation?.selectedLocation;
  const roomRentalLocation = state.bookingCreation?.roomRentalLocation;
  const locations = state.booking?.useCaseLocations ?? [];
  const pickedLocationId = state.apartment?.selectedApartment?.locationId;
  const picked = pickedLocationId
    ? locations.find((location) => location.id === pickedLocationId)
    : undefined;
  // A room-family flow books a picked room (`apartment.selectedApartment`).
  // The Redux value is read both as a flow value (the apartment flow's alias
  // `"room"`, via `flowUseCase`) and as a use case written directly — this
  // site has always accepted both.
  const flowValue = state.bookingCreation?.useCase;
  const roomFlow = isRoomFamily(flowUseCase(flowValue)) || isRoomFamily(flowValue);

  if (roomFlow && picked) return zoneOfLocation(picked);
  if (selectedLocation) return zoneOfLocation(selectedLocation);
  if (picked) return zoneOfLocation(picked);

  const city = roomRentalLocation?.city;
  if (city) {
    const district = roomRentalLocation.district;
    return sharedZoneOf(
      locations.filter(
        (location) =>
          cityOf(location) === city &&
          (!district || district === "all" || districtOf(location) === district)
      )
    );
  }
  return STATION_TIME_ZONE;
};

export const useSelectedLocationTimeZone = (): string =>
  useSelector(selectSelectedLocationTimeZone);

/**
 * The clock a room/venue date step offers its days and times on (#3219): the
 * room picked object-first (#3035) — its location's zone — else the booking
 * flow's (`selectSelectedLocationTimeZone`: the picked room, the selection,
 * the search area). The ONE resolver both the apartment and the venue step
 * use, and what they pass to `useBookingWindow`.
 */
export const selectPickedRoomTimeZone = (
  state: RootState,
  pickedRoomLocationId?: string | null
): string =>
  pickedRoomLocationId
    ? zoneOfLocationId(state.booking?.useCaseLocations ?? [], pickedRoomLocationId)
    : selectSelectedLocationTimeZone(state);

export const usePickedRoomTimeZone = (pickedRoomLocationId?: string | null): string =>
  useSelector((state: RootState) => selectPickedRoomTimeZone(state, pickedRoomLocationId));

/** What a booking, order or plan row may say about its location. */
export interface BookingLocationRef {
  locationTimeZone?: string | null;
  locationId?: string | null;
}

/**
 * The zone of a booking the screen HOLDS — an order, a ticket, a plan row —
 * never the booking flow's selection (#3219). Its own `locationTimeZone` (the
 * order and plan payloads carry it); for a response that predates the field,
 * the zone of its location in the tenant's location list; otherwise the
 * default.
 */
export const selectBookingTimeZone = (
  state: RootState,
  booking?: BookingLocationRef | null
): string => {
  if (booking?.locationTimeZone) {
    return zoneOfLocation({ timeZone: booking.locationTimeZone });
  }
  return zoneOfLocationId(state.booking?.locations ?? [], booking?.locationId);
};

/** The zone of the location with this id in a location list (`GET location`
 *  rows carry `timeZone`); the default when the id is absent or not listed. */
export const zoneOfLocationId = (
  locations: readonly LocationPros[],
  locationId?: string | null
): string =>
  zoneOfLocation(
    locationId ? locations.find((candidate) => candidate.id === locationId) : undefined
  );

export const useBookingTimeZone = (booking?: BookingLocationRef | null): string =>
  useSelector((state: RootState) => selectBookingTimeZone(state, booking));
